/**
 * # as-tabs
 *
 * A tabbed container that shows one child panel at a time.
 * Each direct child becomes a tab panel. The tab label is taken from
 * the child's `tab-label` attribute.
 *
 * ## Attributes
 * - `active`: The index of the initially active tab (default: 0)
 * - `accordion`: When present, clicking the active tab toggles the panel
 *   open/closed. A caret on the active tab indicates the collapsed state.
 *
 * ## Events
 * - `as-tab-change`: Dispatched when the active tab changes
 *   - Detail: `{ index: number, label: string }`
 *
 * ## Usage
 * ```html
 * <as-tabs accordion>
 *   <div tab-label="Welcome">Welcome content</div>
 *   <div tab-label="News">News content</div>
 *   <div tab-label="Help">Help content</div>
 * </as-tabs>
 * ```
 */
import styleText from './as-tabs.css?raw';

export class AsTabs extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private activeIndex = 0;
  private collapsed = false;

  private get isAccordion() {
    return this.hasAttribute('accordion');
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsTabs.sheet];

    this.shadowRoot!.innerHTML = /*html*/`
      <div class="tab-bar"></div>
      <div class="tab-panels">
        <slot></slot>
      </div>
    `;
  }

  connectedCallback() {
    this.activeIndex = parseInt(this.getAttribute('active') || '0', 10);

    const slot = this.shadowRoot!.querySelector('slot')!;
    slot.addEventListener('slotchange', () => this.buildTabs());

    this.buildTabs();
  }

  private buildTabs() {
    const panels = this.getPanels();
    const bar = this.shadowRoot!.querySelector('.tab-bar')!;

    bar.innerHTML = '';
    panels.forEach((panel, i) => {
      const btn = document.createElement('button');
      btn.className = 'tab-button';

      const label = document.createElement('span');
      label.textContent = panel.getAttribute('tab-label') || `Tab ${i + 1}`;
      btn.appendChild(label);

      if (this.isAccordion) {
        const caret = document.createElement('span');
        caret.className = 'caret';
        btn.appendChild(caret);
      }

      btn.addEventListener('click', () => this.handleTabClick(i));
      bar.appendChild(btn);
    });

    this.setActive(this.activeIndex);
  }

  private getPanels(): Element[] {
    const slot = this.shadowRoot!.querySelector('slot')!;
    return slot.assignedElements();
  }

  private handleTabClick(index: number) {
    if (this.isAccordion && index === this.activeIndex) {
      this.collapsed = !this.collapsed;
      this.updateCollapsed();
      return;
    }
    this.collapsed = false;
    this.setActive(index);
  }

  private updateCollapsed() {
    const panelsEl = this.shadowRoot!.querySelector('.tab-panels') as HTMLElement;
    if (this.collapsed) {
      panelsEl.classList.add('collapsed');
      this.setAttribute('collapsed', '');
    } else {
      panelsEl.classList.remove('collapsed');
      this.removeAttribute('collapsed');
    }

    // Update caret direction on active button
    const buttons = this.shadowRoot!.querySelectorAll('.tab-button');
    buttons.forEach((btn, i) => {
      if (i === this.activeIndex && this.collapsed) {
        btn.classList.add('collapsed');
      } else {
        btn.classList.remove('collapsed');
      }
    });
  }

  setActive(index: number) {
    const panels = this.getPanels();
    if (index < 0 || index >= panels.length) return;

    this.activeIndex = index;

    panels.forEach((panel, i) => {
      if (i === index) {
        panel.setAttribute('active', '');
      } else {
        panel.removeAttribute('active');
      }
    });

    const buttons = this.shadowRoot!.querySelectorAll('.tab-button');
    buttons.forEach((btn, i) => {
      if (i === index) {
        btn.setAttribute('active', '');
      } else {
        btn.removeAttribute('active');
        btn.classList.remove('collapsed');
      }
    });

    this.updateCollapsed();

    this.dispatchEvent(new CustomEvent('as-tab-change', {
      bubbles: true,
      composed: true,
      detail: { index, label: panels[index]?.getAttribute('tab-label') || '' }
    }));
  }
}

if (!customElements.get('as-tabs')) {
  customElements.define('as-tabs', AsTabs);
}
